import * as React from 'react';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { Dialog, DialogUtils } from '@shared/ui-react/Dialog';
import { observer } from 'mobx-react';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { DocumentPropertiesService, IDocumentProperty, IDocumentPropertyAssign } from '@shared/services/DocumentPropertiesService';
import { Button } from '@shared/ui-react/Button';
import { Notify } from '@shared/ui-native/Notify';
import { InputNumber } from 'primereact/inputnumber';
import './PropertiesFillDialog.scss';
import './PropertiesAssignDialog.scss';
import { Events } from '@shared/Events';
import { SingleMultiSelect } from '@shared/ui-react/SingleMultiSelect';
import { cs } from '@shared/utils/utils-classname';

export const PropertiesFillDialog = {
    openForFolder: (folderUuid: string, fileUuids?: string[], onSaved?: (values: any) => void) => {
        const targetEl = document.getElementById('react-portal-root');
        ReactUtils.safeRender(
            <Dialog onClose={PropertiesFillDialog.close} title={__('js_properties.assign.document_properties')}>
                <div className='padded card' style={{ maxWidth: '800px' }}>
                    <PropertiesFileFillComponent folderUuid={folderUuid} fileUuids={fileUuids} onSaved={onSaved}></PropertiesFileFillComponent>
                </div>
            </Dialog>,
            targetEl
        );
    },

    close: () => {
        ReactUtils.safeUnmountComponentAtNode(document.getElementById('react-portal-root'));
    },
};

@observer
export class PropertiesFileFillComponent extends React.Component<{
    folderUuid: string;
    fileUuids?: string[];
    onSaved?: (values: any) => void;
    onInitialized?: () => void;
    onValidationStatusChanged?: (state: boolean) => void;
}> {
    fillComponent: PropertiesFillComponent;

    render() {
        const isSingleFile = this.props.fileUuids?.length === 1;

        return (
            <PropertiesFillComponent
                ref={el => (this.fillComponent = el)}
                fetchAvailableProperties={() => DocumentPropertiesService.folder.list(this.props.folderUuid)}
                fetchFillValues={
                    !this.props.fileUuids
                        ? null
                        : () => {
                              if (isSingleFile) {
                                  return DocumentPropertiesService.document.fetchDocument(this.props.fileUuids[0]).then(document => {
                                      if (document.properties) {
                                          let fillValues = {};
                                          document.properties.forEach(p => {
                                              fillValues[p.key] = p.value;
                                          });
                                          return fillValues;
                                      }
                                      return {};
                                  });
                              } else {
                                  return Promise.resolve({});
                              }
                          }
                }
                save={
                    this.props.fileUuids
                        ? (fillValues, properties) => {
                              return Promise.allSettled(this.props.fileUuids.map(uuid => DocumentPropertiesService.document.saveValues(uuid, fillValues, properties)));
                          }
                        : null
                }
                onSaved={this.props.onSaved}
                bulkEdit={this.props.fileUuids?.length > 1}
                filesCount={this.props.fileUuids?.length}
                onInitialized={this.props.onInitialized}
                onValidationStatusChanged={this.props.onValidationStatusChanged}
            />
        );
    }
}

@observer
export class PropertiesFillComponent extends React.Component<{
    fetchAvailableProperties: () => Promise<IDocumentPropertyAssign[]>;
    fetchFillValues?: () => Promise<Record<string, any>>;
    save?: (fillValues, properties) => Promise<any>;
    onSaved?: (values: any) => void;
    manualAddDelete?: boolean;
    showInfoColumns?: boolean;
    bulkEdit?: boolean;
    filesCount?: number;
    maxHeight?: number | 'auto';
    onValidationStatusChanged?: (state: boolean) => void;
    onInitialized?: () => void;

    // as controlled component
    fillValues?: Record<string, any>;
    onFillValuesChanged?: (fill: Record<string, any>, logicSwitches?: Record<string, 'and' | 'or'>) => void;

    logicSwitches?: Record<string, 'and' | 'or'>;
    onLogicSwitchChanged?: (propKey: string, value: 'and' | 'or') => void;
}> {
    private canEdit = kuura.hasPrivilege('Edit_Document');
    @observable isLoading = false;
    @observable properties: Record<string, IDocumentPropertyAssign> = {};
    @observable fillValues: Record<string, any | any[]> = {};

    // for manual adding mode
    @observable notAddedProperties: string[] = [];
    @observable propertyToAdd: string = null;

    // for bulk edit mode
    @observable disabledProperties = {};

    @computed
    get fillValuesNonNull() {
        const result = {};
        Object.keys(this.fillValues).forEach(k => {
            if (this.fillValues[k] !== null) {
                result[k] = this.fillValues[k];
            }
        });
        return result;
    }

    static defaultProps = {
        showInfoColumns: true,
        maxHeight: 500,
    };

    constructor(props: any) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        this.init();
    }

    componentDidUpdate(prevProps) {
        if (this.props.fetchAvailableProperties !== prevProps.fetchAvailableProperties) {
            this.init();
        }

        if (this.props.fillValues !== prevProps.fillValues) {
            this.initFillValues(this.props.fillValues || {}, false);
        }
    }

    init = _.debounce(async () => {
        console.log('init');
        this.isLoading = true;
        this.fillValues = {};

        let properties = {};
        let rawFillValues = this.props.fillValues ?? null;

        await this.props
            .fetchAvailableProperties()
            .then(resp => {
                resp.forEach(p => {
                    const key = p.propertyDeclaration.propertyKey;
                    properties[key] = p;

                    if (this.props.bulkEdit) {
                        this.disabledProperties[key] = true;
                    }
                });

                if (resp.length) {
                    this.propertyToAdd = resp[0].propertyDeclaration.propertyKey;
                }
            })
            .catch(() => {});

        if (this.props.fetchFillValues) {
            rawFillValues = await this.props.fetchFillValues();
            this.propertyToAdd = this.notAddedProperties[0] ?? null;
        }

        runInAction(() => {
            this.properties = properties;
            if (rawFillValues || this.props.manualAddDelete) {
                this.initFillValues(rawFillValues, false);
            }
            this.isLoading = false;

            if (typeof this.props.onInitialized === 'function') {
                this.props.onInitialized();
            }
            this.validate();
        });
    }, 30);

    private initFillValues(rawFillValues: Record<string, any>, hideEmpty = false) {
        rawFillValues = rawFillValues || {};
        const fillValues = {};

        const propertyKeys = Object.keys(this.properties);
        propertyKeys.forEach(pKey => {
            fillValues[pKey] = rawFillValues[pKey] ?? null; // add keys right away so they are observable
        });

        if (hideEmpty && this.props.manualAddDelete) {
            // hide properties with non-null values
            const hiddenProperties = [];
            propertyKeys.forEach(pKey => {
                if (fillValues[pKey] == null) {
                    hiddenProperties.push(pKey);
                }
            });
            this.notAddedProperties = hiddenProperties;
        }

        this.fillValues = fillValues;
    }

    private setFillValue(pKey: string, value: any) {
        if (Array.isArray(value)) {
            value = value.filter(v => v !== '');
            if (value.length === 0) {
                value = null;
            }
        }
        this.fillValues[pKey] = value;
        this.validate();
        this.triggerFillChanged();
    }

    private triggerFillChanged = () => {
        if (typeof this.props.onFillValuesChanged === 'function') {
            this.props.onFillValuesChanged(this.fillValuesNonNull);
        }
    };

    private validate() {
        if (typeof this.props.onValidationStatusChanged === 'function') {
            let isFormValid = true;
            if (Object.keys(this.properties).length === 0) {
                isFormValid = true;
            } else {
                Object.keys(this.properties).forEach(pKey => {
                    if (!this.isFieldValid(pKey)) {
                        isFormValid = false;
                    }
                });
            }
            this.props.onValidationStatusChanged(isFormValid);
        }
    }

    render() {
        let propertiesKeys = Object.keys(this.properties);
        if (this.props.manualAddDelete) {
            propertiesKeys = propertiesKeys.filter(pKey => !this.notAddedProperties.includes(pKey));
        }

        if (this.isLoading) {
            return <div className='overlay-loading' style={{ height: 70, width: '100%' }}></div>;
        }

        if (propertiesKeys.length === 0 && !this.props.manualAddDelete) {
            return (
                <div className='text-center' style={{ padding: 20, fontSize: 16 }}>
                    {__('js_properties.assign.nothing_assigned')}
                </div>
            );
        }

        return (
            <>
                <div className='properties-fill__wrapper flex-column' style={{ maxHeight: this.props.maxHeight }}>
                    {this.props.filesCount > 1 ? (
                        <div style={{ background: 'aliceblue', marginBottom: 20, padding: 7, marginTop: -10 }} className='text-center'>
                            {__('js_files.n_files_selected', this.props.filesCount)}
                        </div>
                    ) : null}
                    <div className='of-a'>
                        <table style={{ tableLayout: 'fixed' }}>
                            <colgroup>
                                {/* todo handle mobile 1-col layout */}
                                <col style={{ minWidth: 150, width: '50%' }} />
                                <col style={{ minWidth: 150, width: '100%' }} />
                                {this.props.showInfoColumns && (
                                    <>
                                        <col style={{ width: '20%' }} />
                                        <col style={{ width: '20%' }} />
                                    </>
                                )}
                                {/*{this.props.manualAddDelete && <col style={{ width: 30 }}/>}*/}
                            </colgroup>

                            {this.props.showInfoColumns && (
                                <thead>
                                    <tr>
                                        <th>{__('js_properties.assign.property')}</th>
                                        <th>{__('js_properties.assign.value')}</th>
                                        <th style={{ paddingLeft: 10 }}>{__('general.type')}</th>
                                        <th className='text-center'>{__('js_properties.editor.number.range')}</th>
                                        {/*{this.props.manualAddDelete && <th></th>}*/}
                                    </tr>
                                </thead>
                            )}

                            <tbody>
                                {propertiesKeys.map(key => {
                                    const prop: IDocumentProperty = this.properties[key]?.propertyDeclaration;
                                    const type = prop.schema?.type;
                                    let label = prop.labels[kuura.getLanguage()] || key;
                                    if (type === 'array' && prop.schema.minItems != 0) {
                                        label += ' *';
                                    }
                                    let min = '';
                                    let max = '';
                                    let minBracket = '';
                                    let maxBracket = '';

                                    if ($.isNumeric(prop.schema.minimum)) {
                                        min = prop.schema.minimum;
                                        minBracket = '[ ';
                                    } else if ($.isNumeric(prop.schema.exclusiveMinimum)) {
                                        min = prop.schema.exclusiveMinimum;
                                        minBracket = '( ';
                                    }

                                    if ($.isNumeric(prop.schema.maximum)) {
                                        max = prop.schema.maximum;
                                        maxBracket = ' ]';
                                    } else if ($.isNumeric(prop.schema.exclusiveMaximum)) {
                                        max = prop.schema.exclusiveMaximum;
                                        maxBracket = ' )';
                                    }

                                    const rangeCell = (
                                        <td className='no-break-text text-center'>
                                            <span>
                                                {minBracket} {min}
                                            </span>
                                            <span> - </span>
                                            <span>
                                                {max} {maxBracket}
                                            </span>
                                        </td>
                                    );

                                    const labelDiv = (
                                        <label className='flex-row ai-center gap-half'>
                                            {this.props.bulkEdit && (
                                                <input type='checkbox' onClick={() => this.toggleDisableProperty(key)} checked={!this.disabledProperties[key]} />
                                            )}
                                            <div className={this.disabledProperties[key] ? 'fade-disable ellipsis' : 'ellipsis'}>{label}</div>
                                        </label>
                                    );

                                    if (!this.canEdit) {
                                        return (
                                            <tr key={key}>
                                                <td title={key}>{label}</td>
                                                <td>{Array.isArray(this.fillValues[key]) ? this.fillValues[key].join(', ') : this.fillValues[key]}</td>

                                                {this.props.showInfoColumns && (
                                                    <>
                                                        <td>{DocumentPropertiesService.typeLabels[type]}</td>
                                                        {rangeCell}
                                                    </>
                                                )}
                                            </tr>
                                        );
                                    }

                                    return (
                                        <tr key={key}>
                                            <td title={key}>{labelDiv}</td>
                                            <td>
                                                <div className='property-title' title={key}>
                                                    {labelDiv}
                                                </div>
                                                {(type === 'integer' || type === 'number') && (
                                                    <InputNumber
                                                        value={this.fillValues[key]}
                                                        onValueChange={e => this.setFillValue(key, e.value)}
                                                        disabled={this.disabledProperties[key]}
                                                        inputStyle={{ width: '100%' }}
                                                        inputClassName={this.isFieldValid(key) ? '' : 'error'}
                                                        mode='decimal'
                                                        useGrouping={false}
                                                        minFractionDigits={1}
                                                        maxFractionDigits={3}
                                                    />
                                                )}

                                                {type === 'string' && (
                                                    <input
                                                        type='text'
                                                        style={{ width: '100%' }}
                                                        className={this.isFieldValid(key) ? '' : 'error'}
                                                        disabled={this.disabledProperties[key]}
                                                        value={this.fillValues[key] == null ? '' : this.fillValues[key]}
                                                        onChange={e => this.setFillValue(key, e.currentTarget.value)}
                                                    />
                                                )}

                                                {type === 'array' && (
                                                    <div className={cs('flex-column', { 'fade-disable': this.disabledProperties[key] })}>
                                                        <SingleMultiSelect
                                                            value={this.getValuesForProp(prop)}
                                                            options={prop.schema.items.enum.map(o => ({ label: o, value: o }))}
                                                            onChange={val => this.setFillValue(key, val)}
                                                            minItems={prop.schema.minItems}
                                                            maxItems={prop.schema.maxItems}
                                                            showError={true}
                                                            logicSwitch={this.props.logicSwitches ? this.props.logicSwitches[key] ?? 'or' : 'or'}
                                                            onChangeLogicSwitch={this.props.onLogicSwitchChanged ? val => this.props.onLogicSwitchChanged(key, val) : null}
                                                        />
                                                    </div>
                                                )}
                                            </td>
                                            {this.props.showInfoColumns && (
                                                <>
                                                    <td style={{ paddingLeft: 10 }} className={this.disabledProperties[key] ? 'fade-disable' : null}>
                                                        {DocumentPropertiesService.typeLabels[type]}
                                                    </td>
                                                    {rangeCell}
                                                </>
                                            )}
                                            {/*{this.props.manualAddDelete && <td style={{ paddingLeft: 0, paddingRight: 0 }}>*/}
                                            {/*    <Button icon='iki iki-cross' className='ik-button-secondary--subtle ik-button-small' onClick={() => this.removeProperty(prop.propertyKey)}/>*/}
                                            {/*</td>}*/}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {this.props.manualAddDelete && this.notAddedProperties.length > 0 && (
                        <footer className='flex-row gap-half' style={{ marginTop: 10 }}>
                            <div className='flex-row' style={{ flexWrap: 'nowrap', gap: 10 }}>
                                <select value={this.propertyToAdd ?? ''} onChange={e => (this.propertyToAdd = e.currentTarget.value)} style={{ maxWidth: 250 }}>
                                    {this.notAddedProperties.length === 0 ? <option disabled>{__('js_properties.assign.nothing_assigned')}</option> : null}
                                    {this.notAddedProperties.map(pKey => (
                                        <option value={pKey} key={pKey}>
                                            {this.properties[pKey].propertyDeclaration.labels[kuura.getLanguage()] || pKey}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <Button icon='iki iki-plus' onClick={this.addSelectedProperty} disabled={!this.propertyToAdd}>
                                {__('general.add')}
                            </Button>
                        </footer>
                    )}

                    {!this.canEdit && <div className='text-center padded-half'>{__('general.validation.no_access_to_edit_values')}</div>}
                </div>

                {this.props.save && (
                    <div className='flex-row' style={{ justifyContent: 'space-between', marginTop: 25 }}>
                        <Button className='ik-button-secondary--subtle' onClick={DialogUtils.close}>
                            {__('general.close')}
                        </Button>
                        {this.canEdit && (
                            <Button
                                className='ik-button-primary right'
                                onClick={this.saveValues}
                                disabled={this.isLoading || propertiesKeys.length === 0 || Object.keys(this.disabledProperties).length === propertiesKeys.length}
                            >
                                {__('general.save')}
                            </Button>
                        )}
                    </div>
                )}
            </>
        );
    }

    addSelectedProperty = action(() => {
        this.notAddedProperties = this.notAddedProperties.filter(p => p !== this.propertyToAdd);
        this.fillValues = {
            // make new keys observable
            ...this.fillValues,
            [this.propertyToAdd]: null,
        };

        this.triggerFillChanged();

        const prop = this.properties[this.propertyToAdd];
        if (prop.propertyDeclaration.schema?.type !== 'array') {
            const inputs = document.querySelectorAll('.properties-fill__wrapper input');
            if (inputs?.length) {
                (inputs[inputs.length - 1] as HTMLInputElement).focus();
            }
        }

        this.propertyToAdd = this.notAddedProperties[0] ?? null;
    });

    removeProperty = action((pKey: string) => {
        if (this.notAddedProperties.length === 0) {
            this.propertyToAdd = pKey;
        }
        this.notAddedProperties.push(pKey);
        this.fillValues[pKey] = null;
        this.triggerFillChanged();
    });

    toggleDisableProperty = (key: string) => {
        if (this.disabledProperties[key]) {
            delete this.disabledProperties[key];
        } else {
            this.disabledProperties[key] = true;
        }
    };

    getValuesForProp = (prop: IDocumentProperty) => {
        let rawValues = this.fillValues[prop.propertyKey];
        // some values might have been deleted from declaration and need to be filtered out from fill values
        if (prop.schema.type === 'array' && Array.isArray(rawValues)) {
            rawValues = rawValues.filter(v => prop.schema.items.enum.includes(v));
        }
        return rawValues;
    };

    saveValues = () => {
        this.isLoading = true;

        const handleError = err => {
            this.isLoading = false;
            if (Array.isArray(err.response?.data?.errorMessages)) {
                Notify.error(err.response?.data?.errorMessages[0]);
            } else {
                Notify.error();
            }
        };

        const fillValuesWithEmpty = {};
        Object.values(this.properties).forEach(p => {
            const key = p.propertyDeclaration.propertyKey;
            if (!this.disabledProperties[key]) {
                fillValuesWithEmpty[p.propertyDeclaration.propertyKey] = this.fillValues[key] ?? null;
            }
        });

        this.props
            .save(
                fillValuesWithEmpty,
                Object.values(this.properties).map(p => p.propertyDeclaration)
            )
            .then(resp => {
                const saveResult = {
                    successNr: resp.filter(r => r.status === 'fulfilled').length,
                    failedNr: resp.filter(r => r.status !== 'fulfilled').length,
                };
                if (saveResult.successNr) {
                    Notify.success(__('js_files.updated_n_files_success', saveResult.successNr));
                }
                if (saveResult.failedNr) {
                    Notify.error(__('js_files.updated_n_files_failed', saveResult.failedNr));
                }

                this.isLoading = false;
                Events.trigger('explorer_items_changed');
                if (typeof this.props.onSaved === 'function') {
                    this.props.onSaved(this.fillValues);
                }
                DialogUtils.close();
            })
            .catch(handleError);
    };

    isFieldValid = (key: string) => {
        const schema = this.properties[key]?.propertyDeclaration?.schema;
        let value = this.fillValues[key];

        if (schema.type === 'integer' || schema.type === 'number') {
            value = parseFloat(value + '');

            if (schema.hasOwnProperty('minimum') && value < parseFloat(schema.minimum)) {
                return false;
            }

            if (schema.hasOwnProperty('maximum') && value > parseFloat(schema.maximum)) {
                return false;
            }

            if (schema.hasOwnProperty('exclusiveMinimum') && value <= parseFloat(schema.exclusiveMinimum)) {
                return false;
            }

            if (schema.hasOwnProperty('exclusiveMaximum') && value >= parseFloat(schema.exclusiveMaximum)) {
                return false;
            }
        }

        if (schema.type === 'array') {
            if (value == null) {
                value = [];
            } else if (!Array.isArray(value)) {
                value = [value];
            }
            if (schema.hasOwnProperty('minItems') && value.length < schema.minItems) {
                return false;
            }
            if (schema.hasOwnProperty('maxItems') && value.length > schema.maxItems) {
                return false;
            }
        }

        return true;
    };
}

// some old library seems to be reverting Promises to old version without allSettled
Promise.allSettled =
    Promise.allSettled ||
    (promises =>
        Promise.all(
            promises.map(p =>
                p
                    .then(value => ({
                        status: 'fulfilled',
                        value,
                    }))
                    .catch(reason => ({
                        status: 'rejected',
                        reason,
                    }))
            )
        ));
